// tippi/src/pages/admin/invoices/AdminFeeInvoices.js
import React, { useMemo, useState } from 'react';
import { invoiceApi } from '../../../firebase/functionsService';
import { downloadInvoicePdf } from '../../../utils/downloadInvoicePdf';
import { useAllFeeInvoices } from '../../../hooks/useAllFeeInvoices';
import InvoiceStatusChip from '../../../components/common/InvoiceStatusChip';
import { formatKr as kr } from '../../../utils/formatKr';
import { COLORS } from '../../../styles/tokens';
import TippiBillingForm from './TippiBillingForm';

const FILTERS = [
  { key: 'all',     label: 'Alle',    test: () => true },
  { key: 'unpaid',  label: 'Ubetalt', test: i => i.status === 'issued' || i.status === 'overdue' },
  { key: 'overdue', label: 'Forfalt', test: i => i.status === 'overdue' },
  { key: 'paid',    label: 'Betalt',  test: i => i.status === 'paid' },
];

const mailText = i => {
  if (i.mailStatus === 'sent') return 'E-post sendt';
  if (i.mailStatus === 'failed') return 'E-post feilet';
  if (i.mailStatus) return 'E-post venter';
  return '';
};

export default function AdminFeeInvoices() {
  const { invoices, loading, error, truncated } = useAllFeeInvoices();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);

  const rows = useMemo(() => {
    const test = FILTERS.find(f => f.key === filter).test;
    const q = query.trim().toLowerCase();
    return invoices.filter(i => test(i) && (!q
      || `${i.invoiceNumber ?? ''} ${i.recipient?.name ?? ''} ${i.period ?? ''} ${i.periodText ?? ''}`.toLowerCase().includes(q)));
  }, [invoices, filter, query]);

  return (
    <>
      <TippiBillingForm />
      {error && <p role="alert" style={styles.error}>Kunne ikke hente fakturaer. Last siden på nytt.</p>}
      <div style={styles.filters}>
        {FILTERS.map(f => (
          <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}
                  style={{ ...styles.filter, ...(filter === f.key ? styles.filterOn : {}) }}>
            {f.label}
          </button>
        ))}
      </div>
      <input aria-label="Søk i gebyrfakturaer" placeholder="Søk etter sjåfør, periode eller fakturanr." value={query}
             onChange={e => setQuery(e.target.value)} style={styles.search} />

      {truncated && <p style={styles.hint}>Viser de siste 500 dokumentene.</p>}
      {loading && rows.length === 0 && <p style={styles.hint}>Laster gebyrfakturaer…</p>}

      {rows.map(i => (
        <FeeRow key={i.id} invoice={i} open={openId === i.id}
                onToggle={() => setOpenId(openId === i.id ? null : i.id)} />
      ))}
      {!loading && rows.length === 0 && <p style={styles.hint}>Ingen gebyrfakturaer funnet.</p>}
    </>
  );
}

function FeeRow({ invoice: inv, open, onToggle }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: 'ok' | 'error', text }
  const [paying, setPaying] = useState(false);
  const due = inv.dueDate?.toDate?.();
  const paid = inv.status === 'paid';
  const panelId = `fee-actions-${inv.id}`;

  async function run(action, okText) {
    setNotice(null);
    setBusy(true);
    try {
      await action();
      if (okText) setNotice({ kind: 'ok', text: okText });
    } catch (err) {
      setNotice({ kind: 'error', text: err?.message || 'Noe gikk galt. Prøv igjen.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="fee-invoice-row" style={styles.row}>
      <div style={styles.rowTop}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={styles.name}>Gebyrfaktura {inv.invoiceNumber}</div>
          <div style={styles.hint}>{inv.periodText ?? inv.period ?? '–'} · <strong>{inv.recipient?.name ?? '–'}</strong></div>
          <div style={styles.hint}>
            {kr(inv.grossAmount)}{due ? ` · forfall ${due.toLocaleDateString('nb-NO')}` : ''}
            {mailText(inv) ? ` · ${mailText(inv)}` : ''}
          </div>
          {inv.status === 'overdue' && (
            <div style={styles.blocked}>Sjåføren er blokkert for fakturaoppdrag</div>
          )}
        </div>
        <InvoiceStatusChip invoice={inv} />
        <button type="button" style={styles.smallBtn} aria-expanded={open} aria-controls={panelId}
                onClick={onToggle}>
          {open ? 'Lukk' : 'Handlinger'}
        </button>
      </div>
      {open && (
        <div id={panelId} style={styles.panel}>
          <div style={styles.actions}>
            <button type="button" style={styles.smallBtn} disabled={busy}
                    onClick={() => run(() => downloadInvoicePdf(inv.id))}>Last ned PDF</button>
            {!paid && (
              <button type="button" style={styles.smallBtn} disabled={busy}
                      onClick={() => run(() => invoiceApi.resendInvoiceMail(inv.id), 'E-posten er sendt på nytt.')}>Send på nytt</button>
            )}
            {!paid && !paying && (
              <button type="button" style={styles.primary} disabled={busy}
                      onClick={() => { setNotice(null); setPaying(true); }}>Merk som betalt</button>
            )}
          </div>
          {!paid && paying && (
            <PaidForm invoice={inv} onCancel={() => setPaying(false)}
                      onDone={() => { setPaying(false); setNotice({ kind: 'ok', text: 'Gebyret er merket som betalt.' }); }} />
          )}
          {notice && (
            <p role={notice.kind === 'error' ? 'alert' : 'status'}
               style={notice.kind === 'error' ? styles.error : styles.okMsg}>
              {notice.text}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function PaidForm({ invoice: inv, onCancel, onDone }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (busy) return;
    setError('');
    if (!reason.trim()) { setError('Skriv en begrunnelse'); return; }
    setBusy(true);
    try {
      await invoiceApi.markFeeInvoicePaid({ invoiceId: inv.id, reason: reason.trim() });
      onDone();
    } catch (err) {
      setError(err?.message || 'Kunne ikke merke som betalt. Prøv igjen.');
      setBusy(false);
    }
  }

  return (
    <div style={styles.form}>
      <label style={styles.label} htmlFor={`paid-reason-${inv.id}`}>Begrunnelse (for eksempel hvordan betalingen er mottatt)</label>
      <textarea id={`paid-reason-${inv.id}`} value={reason} onChange={e => setReason(e.target.value)} rows={2}
                maxLength={500} style={{ ...styles.input, height: 64, padding: 12 }} />
      {error && <p role="alert" style={styles.error}>{error}</p>}
      <div style={styles.actions}>
        <button type="button" style={styles.primary} disabled={busy} onClick={submit}>
          {busy ? 'Lagrer…' : 'Bekreft betalt'}
        </button>
        <button type="button" style={styles.smallBtn} disabled={busy} onClick={onCancel}>Avbryt</button>
      </div>
    </div>
  );
}

const styles = {
  filters:  { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  filter:   { border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textMid, borderRadius: 18, padding: '6px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  filterOn: { border: `1.5px solid ${COLORS.primary}`, color: COLORS.textDark, background: COLORS.surfaceHover },
  search:   { width: '100%', boxSizing: 'border-box', height: 44, padding: '0 14px', borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 14.5, marginBottom: 12, background: COLORS.white, color: COLORS.textDark },
  hint:     { fontSize: 12.5, color: COLORS.textMid, marginTop: 2 },
  blocked:  { fontSize: 12.5, fontWeight: 700, color: COLORS.dangerText, marginTop: 4 },
  row:      { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8 },
  rowTop:   { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  name:     { fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  panel:    { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` },
  smallBtn: { border: `1.5px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, borderRadius: 18, padding: '6px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  form:     { marginTop: 12, display: 'flex', flexDirection: 'column' },
  label:    { fontSize: 12, fontWeight: 600, color: COLORS.textMid, margin: '8px 0 4px' },
  input:    { boxSizing: 'border-box', height: 42, padding: '0 12px', borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 14.5, fontFamily: 'inherit', background: COLORS.white, color: COLORS.textDark },
  error:    { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '8px 12px', borderRadius: 8, fontSize: 13.5, margin: '10px 0 0' },
  okMsg:    { background: COLORS.successBg, color: COLORS.successText, padding: '8px 12px', borderRadius: 8, fontSize: 13.5, margin: '10px 0 0' },
  actions:  { display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' },
  primary:  { background: COLORS.accent, color: '#1a1a2e', border: 'none', borderRadius: 20, padding: '8px 16px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' },
};
